<template>
  <CardWithTitle
    :item-id="itemId"
    :request-status="requestStatus"
    :options-data="cardOptionsWithCSVExportOption"
    @call-option-menu="callOptionMenu"
    @reload="$emit('reload', $event)"
  >
    <v-row
      no-gutters
      class="multibar-chart-container fill-height"
      justify="center"
    >
      <HighCharts
        v-if="hasData"
        :ref="chartRef"
        :key="itemId"
        class="multibar-chart"
        :options="chartOptions"
      />
    </v-row>
    <template #top-right-slot>
      <Badge
        v-if="hasFilterApplied && isEditor"
        type="info"
        :tooltip="$t('components.Badge.label.tooltip.info')"
        :value="$t('components.Badge.label.icon.key')"
        :preset="true"
      />
    </template>
  </CardWithTitle>
</template>

<script>
import { Chart } from 'highcharts-vue';
import highChartsBasics from '@/mixins/highcharts-basics';
import cardsModel from '@/mixins/cards-model';
import exportModel from '@/mixins/export-model';
import CardWithTitle from '@/components/cards/models/CardWithTitle';
import merge from 'lodash/merge';
import colors from '@/utils/colors';
import channelModel from '@/mixins/channel-model';
import Badge from '@/components/Badge';
import {
  removeSeparator,
  splitStringOnSeparator,
} from '@/utils/formatter';
import { mapGetters } from 'vuex';
import { Flags } from '@/plugins/rollout';

export default {
  name: 'MultibarChart',
  components: {
    HighCharts: Chart,
    CardWithTitle,
    Badge,
  },
  mixins: [
    highChartsBasics,
    channelModel,
    cardsModel,
    exportModel,
  ],
  props: {
    requestStatus: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    series: {
      type: [Array, Object],
      default: () => ([]),
    },
    cardOptions: {
      type: [Array, Object],
      default: () => ([]),
    },
    isExpandedCard: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      channel: 'all',
      neutrals: [],
      negatives: [],
      positives: [],
      notclassified: [],
      categories: [],
      isPublicDashboard: false,
    };
  },
  computed: {
    ...mapGetters('featureFlag', [
      'getValue',
    ]),
    hasData() {
      return Object.keys(this.series).length;
    },
    chartOptions() {
      if (!this.hasData) {
        return {};
      }

      this.prepareDataForChart();

      const customOptions = {
        chart: {
          type: 'bar',
        },
        xAxis: {
          lineWidth: 0,
          tickLength: 0,
          categories: this.categories,
        },
        yAxis: {
          gridLineColor: 'transparent',
          min: 0,
          title: '',
          tickColor: 'transparent',
          labels: {
            enabled: false,
          },
          stackLabels: {
            enabled: true,
          },
        },
        plotOptions: {
          bar: {
            borderColor: 'black',
            borderWidth: 0.5,
            pointPadding: 0,
          },
          series: {
            stacking: 'normal',
          },
        },
        legend: {
          enabled: false,
        },
        series: [{
          name: 'Positivos',
          data: this.positives,
          color: colors.positiveColor,
          index: 0,
        }, {
          name: 'Neutros',
          data: this.neutrals,
          color: colors.neutralColor,
          index: 1,
        }, {
          name: 'Negativos',
          data: this.negatives,
          color: colors.negativeColor,
          index: 2,
        }, {
          name: 'Não Classificados',
          data: this.notclassified,
          color: colors.notClassifiedColor,
          index: 3,
        }],
      };

      return merge({}, this.highchartsBasics, this.highchartTheme, customOptions, this.cardOptions);
    },
  },
  async created() {
    const publicDashboardInfo = await this.getValue(Flags.publicDashboardInfo);
    const token = splitStringOnSeparator(publicDashboardInfo, '|')[0] || '';
    this.isPublicDashboard = await Boolean(token);
  },
  methods: {
    prepareDataForChart() {
      this.categories = [];
      this.neutrals = [];
      this.negatives = [];
      this.positives = [];
      this.notclassified = [];

      (this.series[this.channel] || []).forEach((serie, index) => {
        if (!this.isExpandedCard && index >= this.maxItemsToBeDisplayed) return;

        const serieName = this.isPublicDashboard ? removeSeparator(serie.name) : serie.name;

        this.categories.push(serieName);
        this.neutrals.push(serie.neutral);
        this.negatives.push(serie.negative);
        this.positives.push(serie.positive);
        this.notclassified.push(serie.not_classified);
      });
    },
  },
};
</script>

<style scoped lang="scss">
  .multibar-chart {
    width: 100%;
  }
</style>
